import { firstNonEmptyString } from '../../Utils/stringUtils';
import type { components } from '../../Types/api';
import { isIncludedGroup, PRICING_SCHEME, toGroupKey, toPlanKey, toPlanStatus, toProductKey, toTierKey } from './priceSimulationModel';
import type { ItemApprovalStatus, PackageSimulationDto, PlanStatus, PricingSchemeType, TieredPricePointDto } from './priceSimulationModel';

type PlanDto = components['schemas']['Cockpit.Models.PriceSimulation.PlanDto'];
type VossProductType = components['schemas']['Cockpit.Models.Voss.ProductOffer.VossProductType'];

export interface SelectedTier {
    id?: string | number;
    name?: string | null;
}

export interface WorksheetPlan {
    id: string;
    identifier?: string;
    planId: string;
    name: string;
    months: number;
    currentPrice: number;
    newPrice: number;
    adjustmentPercentage: number;
    approvalStatus: ItemApprovalStatus | null;
    status: PlanStatus;
    pricingScheme: PricingSchemeType;
    tieredPrices: TieredPricePointDto[];
    isModified: boolean;
    isKept: boolean;
    revenue: number | null;
    averageDiscount: number | null;
    quantity: number | null;
    isNew: boolean;
}

export interface WorksheetProduct {
    id: string;
    productId?: string;
    name?: string | null;
    productSku: string;
    productType?: VossProductType;
    plans: WorksheetPlan[];
}

export interface WorksheetProductGroup {
    groupKey: string;
    productGroupId?: string;
    groupName?: string | null;
    isIncluded: boolean;
    productGroupType: number;
    products: WorksheetProduct[];
}

export interface WorksheetTier {
    tierKey: string;
    tierName: string;
    isOutdatedVersion: boolean;
    productGroups: WorksheetProductGroup[];
}

export interface PackageWorksheet {
    packageId: string;
    packageName: string;
    metricsPeriodStart: string | null;
    metricsPeriodEnd: string | null;
    tiers: WorksheetTier[];
}

export function transformPackageToWorksheet(apiPackage: PackageSimulationDto | null | undefined, packageId: string, selectedTiers: SelectedTier[] = []): PackageWorksheet | null {
    if (apiPackage === null || apiPackage === undefined) return null;
    if (apiPackage.tiers === null || apiPackage.tiers === undefined) return null;

    const tiersToProcess = selectedTiers.length > 0 ? apiPackage.tiers.filter((tier) => selectedTiers.some((st) => st.id === tier.tierId)) : apiPackage.tiers;

    if (tiersToProcess.length === 0) return null;

    const plansByIdentifier: Record<string, PlanDto> = {};
    (apiPackage.availablePlans ?? []).forEach((plan) => {
        plansByIdentifier[firstNonEmptyString(plan.identifier, plan.name)] = plan;
    });

    const metricsPeriodStart = apiPackage.metricsPeriodStart ?? null;
    const hasReport = metricsPeriodStart !== null;

    return {
        packageId,
        packageName: apiPackage.packageName ?? 'Package',
        metricsPeriodStart,
        metricsPeriodEnd: apiPackage.metricsPeriodEnd ?? null,
        tiers: tiersToProcess.map((tier) => {
            const { tierId } = tier;

            return {
                tierKey: toTierKey(packageId, tierId),
                tierName: tier.name ?? `Tier ${tierId}`,
                isOutdatedVersion: tier.isOutdatedVersion ?? false,
                productGroups: (tier.productGroups ?? []).map((group) => {
                    const { groupId } = group;
                    const isIncluded = isIncludedGroup(group);

                    return {
                        groupKey: toGroupKey(packageId, groupId),
                        productGroupId: groupId,
                        groupName: group.name,
                        isIncluded,
                        productGroupType: isIncluded ? 1 : 0,
                        products: (group.products ?? []).map((product) => {
                            const { productId } = product;

                            const plans: WorksheetPlan[] = Object.entries(product.prices ?? {}).map(([planIdentifier, pricePoint]) => {
                                const planDef = plansByIdentifier[planIdentifier];
                                const quantity = pricePoint.quantity ?? null;

                                return {
                                    id: toPlanKey(packageId, tierId, planIdentifier, productId),
                                    identifier: planIdentifier,
                                    planId: planDef?.planId ?? planIdentifier,
                                    name: planDef?.name ?? planIdentifier,
                                    months: extractMonths(planIdentifier),
                                    currentPrice: pricePoint.currentPrice ?? 0,
                                    newPrice: pricePoint.newPrice ?? pricePoint.currentPrice ?? 0,
                                    adjustmentPercentage: pricePoint.adjustmentPercentage ?? 0,
                                    approvalStatus: pricePoint.approvalStatus ?? null,
                                    status: toPlanStatus(pricePoint.approvalStatus),
                                    pricingScheme: pricePoint.pricingScheme ?? PRICING_SCHEME.Flat,
                                    tieredPrices: pricePoint.tieredPrices ?? [],
                                    isModified: pricePoint.isModified ?? false,
                                    isKept: pricePoint.isKept ?? false,
                                    revenue: pricePoint.revenue ?? null,
                                    averageDiscount: pricePoint.averageDiscountPercentage ?? null,
                                    quantity,
                                    isNew: hasReport && quantity === null,
                                };
                            });

                            return {
                                id: toProductKey(packageId, tierId, groupId, productId),
                                productId,
                                name: product.name,
                                productSku: firstNonEmptyString(product.articleNumber, productId),
                                productType: product.type,
                                plans,
                            };
                        }),
                    };
                }),
            };
        }),
    };
}

function extractMonths(planIdentifier: string | null | undefined): number {
    const wrapped = planIdentifier?.match(/\((\d+)M\)/i);
    if (wrapped) return parseInt(wrapped[1], 10);
    const bare = planIdentifier?.match(/^(\d+)M$/i);
    if (bare) return parseInt(bare[1], 10);
    return 1;
}

export default transformPackageToWorksheet;
